import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { FileText, ListTodo, Paperclip, PanelRightClose, Quote } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ChatMessage } from '@/lib/collab/use-chat-collab'
import { collectWorkspaceItems } from '@/lib/chat/workspace-items'
import { useBrowserSession } from '@/lib/browser/use-browser-session'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ghostButton, mono } from '@/components/assistant-ui/elements/surfaces'
import { cn } from '@/lib/utils'
import { ArtifactList } from './artifact-list'
import { CitationList } from './citations'
import { BrowserActions, BrowserView } from './browser-panel'
import { formatFileSize } from './time'

interface WorkspaceSidebarProps {
  projectId: string
  threadId: string
  messages: ChatMessage[]
  /** The thread has used the shared browser — show the live view (PLAN-083). */
  browserActive: boolean
  onClose: () => void
}

function Section({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof FileText
  title: string
  count: number
  children: React.ReactNode
}) {
  if (count === 0) return null
  return (
    <section className="flex flex-col gap-2 px-4 py-3">
      <h3 className="text-foreground/70 flex items-center gap-2 text-xs font-semibold tracking-wide uppercase">
        <Icon className="size-3.5" />
        {title}
        <span className={cn(mono, 'text-foreground/35 ms-auto tabular-nums')}>{count}</span>
      </h3>
      {children}
    </section>
  )
}

/**
 * Workspace panel next to the thread: the shared browser (when the thread
 * uses one) and everything the conversation produced or referenced —
 * artifacts the agent created/changed, documents cited, files attached — as
 * quick links into the project. Empty sections are hidden; the route only
 * offers the panel when there is something to show.
 */
export function WorkspaceSidebar({
  projectId,
  threadId,
  messages,
  browserActive,
  onClose,
}: WorkspaceSidebarProps) {
  const items = useMemo(() => collectWorkspaceItems(messages), [messages])

  return (
    <aside
      data-slot="workspace-sidebar"
      className="bg-background border-border flex h-full flex-col border-s"
    >
      {browserActive ? (
        <BrowserWorkspace
          projectId={projectId}
          threadId={threadId}
          items={items}
          onClose={onClose}
        />
      ) : (
        <>
          <Header onClose={onClose} />
          <ScrollArea className="flex-1">
            <LinkedItems projectId={projectId} items={items} />
          </ScrollArea>
        </>
      )}
    </aside>
  )
}

function Header({ actions, onClose }: { actions?: React.ReactNode; onClose: () => void }) {
  const { t } = useTranslation('chat')
  return (
    <div className="border-border flex items-center gap-2 border-b px-4 py-2">
      <h2 className="text-sm font-semibold">{t('workspace.title')}</h2>
      <div className="ms-auto flex items-center gap-2">
        {actions}
        {actions && <span className="bg-border h-5 w-px" aria-hidden />}
        <button
          type="button"
          aria-label={t('workspace.close')}
          onClick={onClose}
          className={cn(ghostButton, 'size-7')}
        >
          <PanelRightClose className="size-4" />
        </button>
      </div>
    </div>
  )
}

/** With a browser: the view fills the panel, linked items sit below it. */
function BrowserWorkspace({
  projectId,
  threadId,
  items,
  onClose,
}: {
  projectId: string
  threadId: string
  items: ReturnType<typeof collectWorkspaceItems>
  onClose: () => void
}) {
  const session = useBrowserSession(projectId, threadId)
  return (
    <>
      <Header actions={<BrowserActions session={session} />} onClose={onClose} />
      <BrowserView session={session} className="min-h-0 flex-1" />
      {items.count > 0 && (
        <div className="border-border max-h-[40%] shrink-0 overflow-y-auto border-t">
          <LinkedItems projectId={projectId} items={items} />
        </div>
      )}
    </>
  )
}

function LinkedItems({
  projectId,
  items,
}: {
  projectId: string
  items: ReturnType<typeof collectWorkspaceItems>
}) {
  const { t } = useTranslation('chat')
  const { tasks, documents, citations, attachments } = items
  return (
    <>
      <Section icon={ListTodo} title={t('workspace.tasks')} count={tasks.length}>
        <ArtifactList
          artifacts={tasks}
          projectId={projectId}
          className="flex flex-col gap-2 [&>*]:max-w-none"
        />
      </Section>
      <Section icon={FileText} title={t('workspace.documents')} count={documents.length}>
        <ArtifactList
          artifacts={documents}
          projectId={projectId}
          className="flex flex-col gap-2 [&>*]:max-w-none"
        />
      </Section>
      <Section icon={Quote} title={t('workspace.sources')} count={citations.length}>
        <CitationList citations={citations} projectId={projectId} />
      </Section>
      <Section icon={Paperclip} title={t('workspace.attachments')} count={attachments.length}>
        <ul className="flex flex-col gap-1.5">
          {attachments.map((att) => {
            const label = (
              <>
                <Paperclip className="size-3.5 shrink-0 text-blue-500" />
                <span className="truncate">{att.filename}</span>
                <span className={cn(mono, 'text-foreground/35 ms-auto shrink-0')}>
                  {att.documentId
                    ? t('workspace.document')
                    : att.size
                      ? formatFileSize(att.size)
                      : ''}
                </span>
              </>
            )
            const cls =
              'text-foreground/80 hover:text-foreground flex items-center gap-2 text-[13px]'
            return (
              <li key={att.id}>
                {att.documentId ? (
                  <Link
                    to="/$projectId/write/$docId"
                    params={{ projectId, docId: att.documentId }}
                    className={cls}
                  >
                    {label}
                  </Link>
                ) : (
                  <a href={att.url} target="_blank" rel="noopener noreferrer" className={cls}>
                    {label}
                  </a>
                )}
              </li>
            )
          })}
        </ul>
      </Section>
    </>
  )
}
